/* Re-usable UI primitives, all sharing the StudioOps design tokens. */
import React from "react";

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...rest
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-all active:scale-[0.98] focus-ring disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };
  const variants = {
    primary:
      "bg-brand text-white hover:bg-brand-hover shadow-glow hover:shadow-glowHover",
    secondary:
      "bg-bg-surface text-ink-primary border border-white/10 hover:bg-bg-surfaceHover hover:border-white/20",
    ghost:
      "bg-transparent text-ink-secondary hover:text-ink-primary hover:bg-white/5",
    danger:
      "bg-err/10 text-err border border-err/30 hover:bg-err/20",
  };
  return (
    <button
      className={`${base} ${sizes[size] || sizes.md} ${variants[variant] || variants.primary} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Input({ className = "", ...rest }) {
  return (
    <input
      className={`w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all ${className}`}
      {...rest}
    />
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select
      className={`w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all appearance-none ${className}`}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Label({ children, htmlFor, className = "" }) {
  return (
    <label
      htmlFor={htmlFor}
      className={`block text-[11px] font-medium text-ink-secondary uppercase tracking-wider mb-1.5 ${className}`}
    >
      {children}
    </label>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <div className={`bg-bg-surface border border-white/10 rounded-xl ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Badge({ children, tone = "default", className = "" }) {
  const tones = {
    default: "bg-white/5 text-ink-secondary border-white/10",
    brand: "bg-brand/10 text-brand border-brand/30",
    ok: "bg-ok/10 text-ok border-ok/30",
    warn: "bg-warn/10 text-warn border-warn/30",
    err: "bg-err/10 text-err border-err/30",
    purple: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md border ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function Spinner({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="animate-spin text-brand"
      fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center fade-in">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-bg-surface border border-white/10 flex items-center justify-center mb-5">
          <Icon size={22} strokeWidth={1.5} className="text-ink-secondary" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-ink-primary mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-ink-secondary max-w-sm mb-5">{description}</p>
      )}
      {action}
    </div>
  );
}
